<template>
    <div class='package' :class='"package--" + slug'>

        <div class='package__carousel carousel' ref='carousel'>
            <Swiper
                class='package__carousel__swiper carousel__swiper'
                effect='creative'
                @afterInit='swiperInit'
                :speed='250'
                :lazyPreloadPrevNext='2'
                :autoplay='{
                    delay: autoplaySpeed,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false
                }'
                :pagination='{
                    clickable: true
                }'
                :cubeEffect="{
                    shadow: false,
                    slideShadows: false
                }"
                :creativeEffect='{
                    limitProgress: 1,
                    prev: {
                        shadow: false,
                        translate: ["-33%", 0, -400],
                        opacity: 0,
                        rotate: [10, 10, 0]
                    },
                    next: {
                        shadow: false,
                        translate: ["33%", 0, -400],
                        opacity: 0,
                        rotate: [-10, -10, 0]
                    }
                }'
                :modules='swiperModules'
                :observer='true'>

                <template v-for='(image, i) in images' :key='`img-${i}`'>
                    <SwiperSlide :style='image.slideStyles'>
                        <div class='carousel__img'>
                            <img :src='image.src' :alt='image.alt' :style='image.imgStyles' loading='lazy' />
                        </div>
                        <!-- <div class='swiper-lazy-preloader' /> -->
                    </SwiperSlide>
                </template>
            </Swiper>
        </div>

        <div class='package__hd'>
            <div class='package__hd__title'>
                <h3 v-html='package.display' />
            </div>
        </div>

        <div class='package__cta'>
            <a class='cta' 
                :class='{
                    "cta--alt": !isCtaBold,
                    "cta--disabled": !package.variant.can_buy
                }' 
                :aria-label='`Order ${package.name} Package for $${price}`'
                :href='`cart/${package.variant.id}:1`'
                role='button'
                tabindex='0'>
                
                <template v-if='!package.variant.can_buy'>
                    <span>Sold Out</span>
                </template>
                <template v-else>
                    <span>Order Package</span>
                    <span class='cta__price'>
                        <span class='cta__price__sign' v-text='"$"' />
                        <span class='cta__price__dollars' v-text='price.dollars' />
                        <span class='cta__price__cents' v-text='"." + price.cents' />
                    </span>
                </template>
            </a>
        </div>

        <ul class='package__items'>
            <template 
                v-for='(item, i) in package.package_items'
                :key='`item-${i}`'>

                <PackageItem
                    :title='item.display'
                    :details='item.description'
                    :featured='item.is_featured'
                    :included='true'
                />
            </template>
            <template
                v-if='emptyItems > 0'
                v-for='(emptyItem, i) in emptyItems'
                :key='`emptyitem-${i}`'>

                <PackageItem
                    :included='false'
                />
            </template>
        </ul>

    </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Navigation, Pagination, A11y, EffectCreative, EffectCube } from 'swiper';
import PackageItem from '^/components/elements/PackageItem.vue';

export default {
    name: 'PackageBlock',
    props: {
        package: {
            type: Object,
            required: true
        },
        slug: {
            type: String,
            required: true
        },
        isCtaBold: {
            type: Boolean,
            default: false
        },
        index: {
            type: Number,
            default: 0
        },
        maxPackageItems: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        swiper: null,
        swiperModules: [Autoplay, Navigation, Pagination, A11y, EffectCreative, EffectCube],
        autoplaySpeed: 5000,
        isCarouselInView: false,
        carouselStartTimeout: null,
        carouselIntersectionObserver: null
    }),
    computed: {
        images () {
            const images = [];
            this.package.package_items.forEach((item) => {
                item.images.forEach((image) => {
                    const scale = image.layout?.scale || 1;
                    const flexAlign = image.layout?.align_vertical === 'bottom' ? 'flex-end' : image.layout?.align_vertical === 'top' ? 'flex-start' : 'center';
                    const transformOrigin = image.layout?.align_vertical === 'bottom' ? 'center bottom' : 'center center';
                    const translate_x_units = image.layout?.translate_x_units || '%';
                    const translate_y_units = image.layout?.translate_y_units || '%';

                    let rotate = image.layout?.rotate || 0;
                    let translate_x = image.layout?.translate_x || 0;
                    let translate_y = image.layout?.translate_y || 0;

                    if (rotate && image.layout?.rotate_random) {
                        const max = Math.abs(rotate);
                        const min = -max;
                        rotate = Math.floor(Math.random() * (max - min + 1) + min);
                    }
                    if (translate_x && image.layout?.translate_x_random) {
                        const max = Math.abs(translate_x);
                        const min = -max;
                        translate_x = Math.floor(Math.random() * (max - min + 1) + min);
                    }
                    if (translate_y && image.layout?.translate_y_random) {
                        const max = Math.abs(translate_y);
                        const min = -max;
                        translate_y = Math.floor(Math.random() * (max - min + 1) + min);
                    }

                    image.slideStyles = {
                        'align-items': flexAlign
                    }
                    image.imgStyles = {
                        'transform': `scale(${scale}, ${scale}) translate(${translate_x}${translate_x_units}, ${translate_y}${translate_y_units}) rotate(${rotate}deg)`,
                        'transform-origin': transformOrigin
                    }
                    images.push(image);
                });
            });

            return images;
        },
        price () {
            const price = this.package.product.price;
            const dollars = Math.floor(price / 100).toString();
            const cents = (price % 100).toString().padStart(2, '0');
            return { dollars, cents };
        },
        emptyItems () {
            return this.maxPackageItems - this.package.package_items.length;
        }
    },
    mounted () {
        this.carouselIntersectionObserver = new window.IntersectionObserver(this.onCarouselIntersect);
        this.carouselIntersectionObserver.observe(this.$refs.carousel)
        window.addEventListener('appResize', this.onAppResize);
    },
    beforeUnmount () {
        this.carouselIntersectionObserver?.disconnect()
        window.removeEventListener('appResize', this.onAppResize);
    },
    methods: {
        swiperInit (swiper) {
            this.swiper = swiper;
            swiper.autoplay.stop(); // started by intersectionobserver
        },
        startCarousel () {
            const interval = 500;
            const slides = 3;
            const stagger = Math.floor(this.autoplaySpeed / slides);
            const maxDelay = interval + (stagger * (slides - 1));
            const time = new Date().getTime();
            const delay = (interval - (time % interval) + this.index * stagger) % maxDelay;

            if (this.carouselStartTimeout) {
                clearTimeout(this.carouselStartTimeout);
            }

            this.carouselStartTimeout = setTimeout(() => {
                this.swiper?.autoplay.start();
            }, delay);
        },
        stopCarousel () {
            if (this.carouselStartTimeout) {
                clearTimeout(this.carouselStartTimeout);
            }

            this.swiper?.autoplay.stop();
        },
        onCarouselIntersect (entries) {
            if (entries[0].isIntersecting) {
                this.isCarouselInView = true;
                this.startCarousel();
            } else {
                this.isCarouselInView = false;
                this.stopCarousel();
            }
        },
        onAppResize () {
            this.swiper?.update();
        }
    },
    components: {
        PackageItem,
        Swiper,
        SwiperSlide
    }
}
</script>